import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AFFILIATE_NETWORKS } from './affiliateNetworks';
import type { Brand } from '@hygh-vybe/lib';
import { API_BASE } from '../../config';

export function AdminAffiliates() {
  const { authFetch } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchBrands() {
      try {
        const res = await authFetch(`${API_BASE}/v1/brands`);
        const json = await res.json();
        if (res.ok && json.data) {
          setBrands(Array.isArray(json.data) ? json.data : []);
        }
      } catch {
        // Non-critical
      } finally {
        setLoading(false);
      }
    }

    fetchBrands();
  }, [authFetch]);

  if (loading) {
    return <div className="admin-loading">Loading affiliates...</div>;
  }

  // Group brands by affiliate network
  const brandsByNetwork = new Map<string, Brand[]>();
  for (const brand of brands) {
    if (brand.affiliateNetwork) {
      const list = brandsByNetwork.get(brand.affiliateNetwork) || [];
      list.push(brand);
      brandsByNetwork.set(brand.affiliateNetwork, list);
    }
  }

  const totalAffiliateBrands = brands.filter((b) => b.affiliateNetwork).length;

  return (
    <div>
      <h1>Affiliate Networks</h1>

      <div className="admin-stats" style={{ marginBottom: '24px' }}>
        <div className="admin-stat-card">
          <div className="stat-label">Networks</div>
          <div className="stat-value">{AFFILIATE_NETWORKS.length}</div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-label">Brands with Affiliate</div>
          <div className="stat-value">
            <Link to="/admin/brands?filter=affiliates" style={{ color: 'inherit', textDecoration: 'underline' }}>
              {totalAffiliateBrands}
            </Link>
          </div>
        </div>
      </div>

      {/* Network cards */}
      {AFFILIATE_NETWORKS.map((network) => {
        const networkedBrands = brandsByNetwork.get(network.name) || [];
        return (
          <div key={network.name} className="admin-scraper-section" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0' }}>{network.name}</h3>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  {network.type} &middot;{' '}
                  <a href={network.website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                    {network.website.replace('https://', '')}
                  </a>
                </p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                  {network.notes}
                </p>
              </div>
              <span
                className={`admin-badge ${networkedBrands.length > 0 ? 'admin-badge-green' : 'admin-badge-yellow'}`}
                style={{ whiteSpace: 'nowrap' }}
              >
                {networkedBrands.length} brand{networkedBrands.length !== 1 ? 's' : ''}
              </span>
            </div>

            {networkedBrands.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Brand</th>
                        <th>Buy URL</th>
                        <th>All Buyable</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {networkedBrands.map((brand) => (
                        <tr key={brand.brandId}>
                          <td>{brand.brandName}</td>
                          <td style={{ fontSize: '0.8rem', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {brand.buyWebsiteUrl ? (
                              <a href={brand.buyWebsiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                                {brand.buyWebsiteUrl.replace('https://', '').slice(0, 40)}...
                              </a>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)' }}>Not set</span>
                            )}
                          </td>
                          <td>
                            <span className={`admin-badge ${brand.allProductsBuyable ? 'admin-badge-green' : 'admin-badge-yellow'}`}>
                              {brand.allProductsBuyable ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            <Link to={`/admin/brands/${brand.brandId}/edit`} style={{ fontSize: '0.8rem', color: 'var(--color-primary)' }}>
                              Edit
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
